'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { GA_TRACKING_ID } from '@/lib/gtag';
import { environment } from '@/config/environment';
import { isKnownAutomationClient } from '@/lib/user-agent';

interface GoogleAnalyticsProps {
  language?: string;
}

export default function GoogleAnalytics({
  language = 'en',
}: GoogleAnalyticsProps) {
  // Decided after mount: the automation check needs the real screen size, and
  // the scripts load afterInteractive anyway.
  const [allowed, setAllowed] = useState(false);
  useEffect(() => setAllowed(!isKnownAutomationClient()), []);

  // Don't load GA if disabled in environment, missing ID, or a known bot
  if (!environment.analytics.enabled || !GA_TRACKING_ID || !allowed) {
    return null;
  }

  return (
    <>
      {/* Preconnect for faster loading */}
      <link rel="preconnect" href="https://www.googletagmanager.com" />

      {/* Global Site Tag (gtag.js) - Google Analytics - loads after hydration */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            
            gtag('config', '${GA_TRACKING_ID}', {
              page_title: document.title,
              page_location: window.location.href,
              language: '${language}',
              custom_map: {
                'custom_parameter_1': 'language',
                'custom_parameter_2': 'page_type',
                'custom_parameter_3': 'business_type'
              },
              // Enhanced measurement settings
              enhanced_measurement: {
                scrolls: true,
                outbound_clicks: true,
                site_search: true,
                video_engagement: true,
                file_downloads: true,
                page_changes: true
              },
              // Enable debug mode based on environment
              debug_mode: ${environment.debug.console},
              // Send page view on load
              send_page_view: true,
              // Custom parameters
              currency: '${language === 'zh' ? 'TWD' : 'USD'}',
              country: '${language === 'zh' ? 'TW' : 'US'}',
              // User properties
              user_properties: {
                language: '${language}',
                site_section: 'main',
                user_type: 'visitor'
              }
            });

            // Set up enhanced ecommerce
            gtag('config', '${GA_TRACKING_ID}', {
              ecommerce: {
                currency: '${language === 'zh' ? 'TWD' : 'USD'}',
                item_list_name: 'pricing_plans'
              }
            });

            // Track initial page load with enhanced data
            gtag('event', 'page_view', {
              page_title: document.title,
              page_location: window.location.href,
              language: '${language}',
              user_agent: navigator.userAgent,
              screen_resolution: screen.width + 'x' + screen.height,
              viewport_size: window.innerWidth + 'x' + window.innerHeight
            });
          `,
        }}
      />
    </>
  );
}
